<template>
  <el-dialog
    title="创建采购单"
    :visible.sync="dialogVisible"
    width="920px"
    custom-class="create-purchase-order-dialog"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <div class="pay-order-section">
      <div class="section-title">产品明细</div>
      <el-table
        :data="previewList"
        border
        stripe
        size="mini"
        show-summary
        :summary-method="getSummaries"
        v-loading="loading"
        element-loading-text="加载中..."
        max-height="320"
      >
        <el-table-column label="序号" width="50" align="center" prop="index"></el-table-column>
        <el-table-column prop="materialName" label="材料名称" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.materialName || scope.row.productName || "-" }}</template>
        </el-table-column>
        <el-table-column prop="purchaseQuantity" label="采购数量" width="80" align="right"></el-table-column>
        <el-table-column prop="unitPrice" label="单价" width="90" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.unitPrice) }}</template>
        </el-table-column>
        <el-table-column prop="purchaseAmount" label="采购金额" width="90" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.purchaseAmount) }}</template>
        </el-table-column>
        <el-table-column prop="taxRate" label="税率" width="70" align="right">
          <template slot-scope="scope">{{ formatTaxRate(supplierTaxRate) }}</template>
        </el-table-column>
        <el-table-column prop="taxAmount" label="税费" width="80" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.taxAmount) }}</template>
        </el-table-column>
        <el-table-column prop="payableAmount" label="应付金额" width="90" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.payableAmount) }}</template>
        </el-table-column>
      </el-table>
    </div>

    <div class="pay-order-form">
      <div class="section-title">支付信息</div>
      <div class="form-grid">
        <div class="form-item wide">
          <span class="form-label">采购供应商</span>
          <el-select v-model="form.supplierId" size="small" filterable clearable placeholder="请选择" @change="onSupplierChange">
            <el-option
              v-for="supplier in displaySupplierList"
              :key="supplier.supplierId"
              :label="supplier.supplierShortName || supplier.supplierName"
              :value="supplier.supplierId"
            ></el-option>
          </el-select>
          <span class="tax-label">税率：{{ formatTaxRate(supplierTaxRate) }}</span>
        </div>
        <div class="form-item wide">
          <span class="form-label">运费</span>
          <el-input v-model.number="form.freight" size="small" type="number" @input="calcTotal"></el-input>
        </div>
        <div class="form-item wide">
          <span class="form-label">平台优惠</span>
          <el-input v-model.number="form.platformDiscount" size="small" type="number" @input="calcTotal"></el-input>
        </div>
        <div class="form-item wide">
          <span class="form-label">退换货费</span>
          <el-input v-model.number="form.returnExchangeFee" size="small" type="number" @input="calcTotal"></el-input>
        </div>
        <div class="form-item wide">
          <span class="form-label">其他费用</span>
          <el-input v-model.number="form.otherFee" size="small" type="number" @input="calcTotal"></el-input>
        </div>
        <div class="form-item wide">
          <span class="form-label">下单平台</span>
          <el-radio-group v-model="form.orderPlatform" size="small">
            <el-radio label="对公">对公</el-radio>
            <el-radio label="平台">平台</el-radio>
          </el-radio-group>
        </div>
        <div class="form-item wide remark-item">
          <span class="form-label">备注</span>
          <el-input v-model="form.remark" type="textarea" :rows="2" size="small" placeholder="请输入备注" maxlength="500" show-word-limit></el-input>
        </div>
      </div>
      <div class="amount-summary">
        <div class="amount-row">
          <span class="amount-label">应付金额</span>
          <span class="amount-value payable">{{ formatMoney(form.payableAmount) }}</span>
        </div>
        <div class="amount-row">
          <span class="amount-label">实付金额</span>
          <el-input v-model.number="form.actualPaidAmount" size="small" type="number" class="amount-input" @input="onActualPaidInput"></el-input>
        </div>
        <div class="amount-row">
          <span class="amount-label">差额</span>
          <span class="amount-value" :class="amountDiffClass">{{ formatAmountDiff(amountDiff) }}</span>
          <span class="amount-tip">（实付金额 - 应付金额）</span>
        </div>
      </div>
      <div class="preview-tip">同店铺将合为一张采购单，预计生成 {{ estimatedOrderCount }} 张；材料明细按 SKU 材料配置合并，单价取所选供应商材料价；多店铺时运费等费用记入第一张采购单</div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="dialogVisible = false">取 消</el-button>
      <el-button type="primary" size="small" :loading="submitting" :disabled="!previewList.length" @click="submit">确 定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { requestModules } from "@r";

function createEmptyForm() {
  return {
    supplierId: "",
    freight: 0,
    platformDiscount: 0,
    returnExchangeFee: 0,
    otherFee: 0,
    orderPlatform: "对公",
    actualPaidAmount: 0,
    payableAmount: 0,
    remark: "",
  };
}

export default {
  name: "CreatePurchaseOrderDialog",
  props: {
    supplierList: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      submitting: false,
      selectedList: [],
      previewList: [],
      estimatedOrderCount: 0,
      localSupplierList: [],
      shouldSyncActualPaid: true,
      lineProductAmount: 0,
      lineTaxAmount: 0,
      form: createEmptyForm(),
    };
  },
  computed: {
    displaySupplierList() {
      if ((this.localSupplierList || []).length) return this.localSupplierList;
      return this.supplierList || [];
    },
    supplierTaxRate() {
      const supplier = (this.displaySupplierList || []).find(item => item.supplierId == this.form.supplierId);
      if (!supplier || supplier.invoiceTaxRate === undefined || supplier.invoiceTaxRate === null || supplier.invoiceTaxRate === "") {
        return 0;
      }
      const taxRate = parseFloat(supplier.invoiceTaxRate);
      return Number.isNaN(taxRate) ? 0 : taxRate;
    },
    amountDiff() {
      const actualPaidAmount = parseFloat(this.form.actualPaidAmount) || 0;
      const payableAmount = parseFloat(this.form.payableAmount) || 0;
      return parseFloat((actualPaidAmount - payableAmount).toFixed(2));
    },
    amountDiffClass() {
      if (this.amountDiff > 0) return "positive";
      if (this.amountDiff < 0) return "negative";
      return "zero";
    },
  },
  methods: {
    open(selectedList) {
      this.selectedList = selectedList || [];
      this.previewList = [];
      this.estimatedOrderCount = 0;
      this.lineProductAmount = 0;
      this.lineTaxAmount = 0;
      this.form = createEmptyForm();
      this.shouldSyncActualPaid = true;
      this.dialogVisible = true;
      this.ensureSupplierList().finally(() => {
        this.loadPreview();
      });
    },
    ensureSupplierList() {
      if ((this.supplierList || []).length) {
        this.localSupplierList = this.supplierList.slice();
        return Promise.resolve();
      }
      return requestModules
        .fetchSuppliers({ beginIndex: 0, pageSize: 1000 })
        .then(res => {
          this.localSupplierList = res.ldata || [];
        })
        .catch(() => {
          this.localSupplierList = [];
        });
    },
    onSupplierChange() {
      this.shouldSyncActualPaid = true;
      this.loadPreview();
    },
    loadPreview() {
      if (!this.selectedList.length) {
        this.$message.warning("请先选择待采购记录");
        this.dialogVisible = false;
        return;
      }
      this.loading = true;
      requestModules
        .previewCreatePurchaseOrders({
          selectedList: JSON.stringify(this.selectedList),
          supplierId: this.form.supplierId || "",
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "加载产品明细失败");
            this.previewList = [];
            this.lineProductAmount = 0;
            this.lineTaxAmount = 0;
            this.form.payableAmount = 0;
            return;
          }
          this.previewList = this.applyLineTaxRates(res.ldata || []);
          this.estimatedOrderCount = (res.mdata && res.mdata.orderCount) || 0;
          this.recalcLineAmounts();
          this.calcTotal();
          this.syncActualPaidAmount();
        })
        .catch(() => {
          this.$message.error("加载产品明细失败");
          this.previewList = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    applyLineTaxRates(rows) {
      const taxRate = this.supplierTaxRate;
      return (rows || []).map((row, index) => {
        const purchaseAmount = parseFloat(row.purchaseAmount) || 0;
        const taxAmount = parseFloat((purchaseAmount * taxRate).toFixed(2));
        const payableAmount = parseFloat((purchaseAmount + taxAmount).toFixed(2));
        return {
          ...row,
          index: index + 1,
          taxRate,
          taxAmount,
          payableAmount,
        };
      });
    },
    recalcLineAmounts() {
      this.lineProductAmount = parseFloat(
        (this.previewList || []).reduce((sum, row) => sum + (Number(row.purchaseAmount) || 0), 0).toFixed(2)
      );
      this.lineTaxAmount = parseFloat((this.previewList || []).reduce((sum, row) => sum + (Number(row.taxAmount) || 0), 0).toFixed(2));
    },
    calcTotal() {
      const linePayableAmount = (this.previewList || []).reduce((sum, row) => sum + (Number(row.payableAmount) || 0), 0);
      const freight = parseFloat(this.form.freight) || 0;
      const platformDiscount = parseFloat(this.form.platformDiscount) || 0;
      const returnExchangeFee = parseFloat(this.form.returnExchangeFee) || 0;
      const otherFee = parseFloat(this.form.otherFee) || 0;
      this.form.payableAmount = parseFloat((linePayableAmount + freight - platformDiscount + returnExchangeFee + otherFee).toFixed(2));
    },
    onActualPaidInput() {
      if (this.form.actualPaidAmount === "" || this.form.actualPaidAmount === null) {
        this.form.actualPaidAmount = 0;
      }
    },
    syncActualPaidAmount() {
      if (this.shouldSyncActualPaid) {
        this.form.actualPaidAmount = this.form.payableAmount;
        this.shouldSyncActualPaid = false;
      }
    },
    formatMoney(value) {
      if (value === "" || value === null || value === undefined) {
        return "0.00";
      }
      return parseFloat(value).toFixed(2);
    },
    formatTaxRate(value) {
      if (value === "" || value === null || value === undefined) {
        return "0%";
      }
      return (parseFloat(value) * 100).toFixed(0) + "%";
    },
    formatAmountDiff(value) {
      if (value > 0) {
        return "+" + this.formatMoney(value);
      }
      return this.formatMoney(value);
    },
    getSummaries({ columns, data }) {
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (column.property === "purchaseQuantity") {
          sums[index] = (data || []).reduce((sum, row) => sum + (Number(row.purchaseQuantity) || 0), 0);
          return;
        }
        if (["purchaseAmount", "taxAmount", "payableAmount"].includes(column.property)) {
          sums[index] = this.formatMoney((data || []).reduce((sum, row) => sum + (Number(row[column.property]) || 0), 0));
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    buildPaymentPayload() {
      this.calcTotal();
      const payableAmount = parseFloat(this.form.payableAmount) || 0;
      const actualPaidAmount = parseFloat(this.form.actualPaidAmount) || 0;
      return {
        freight: parseFloat(this.form.freight) || 0,
        platformDiscount: parseFloat(this.form.platformDiscount) || 0,
        returnExchangeFee: parseFloat(this.form.returnExchangeFee) || 0,
        otherFee: parseFloat(this.form.otherFee) || 0,
        orderPlatform: this.form.orderPlatform || "对公",
        productAmount: this.lineProductAmount,
        taxAmount: this.lineTaxAmount,
        taxRate: this.supplierTaxRate,
        payableAmount,
        actualPaidAmount,
        amountDiff: parseFloat((actualPaidAmount - payableAmount).toFixed(2)),
        remark: (this.form.remark || "").trim(),
      };
    },
    submit() {
      if (!this.form.supplierId) {
        this.$message.warning("请选择采购供应商");
        return;
      }
      if (!this.selectedList.length || !this.previewList.length) {
        this.$message.warning("没有可创建的采购明细");
        return;
      }
      if (this.submitting) return;
      this.submitting = true;
      const payment = this.buildPaymentPayload();
      requestModules
        .createPurchaseOrders({
          selectedList: JSON.stringify(this.selectedList),
          supplierId: this.form.supplierId,
          ...payment,
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "创建采购单失败");
            return;
          }
          const orderCount = (res.mdata && res.mdata.orderCount) || (res.ldata || []).length || 0;
          this.$message.success(`已创建 ${orderCount} 张采购单`);
          this.dialogVisible = false;
          this.$emit("success", res);
        })
        .catch(() => {
          this.$message.error("创建采购单失败");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    handleClose() {
      this.selectedList = [];
      this.previewList = [];
      this.estimatedOrderCount = 0;
      this.lineProductAmount = 0;
      this.lineTaxAmount = 0;
      this.shouldSyncActualPaid = true;
      this.form = createEmptyForm();
    },
  },
};
</script>

<style lang="scss">
.create-purchase-order-dialog {
  .el-dialog__body {
    padding-top: 10px;
  }

  .pay-order-section {
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;
    padding-left: 8px;
    border-left: 3px solid #409eff;
  }

  .pay-order-form {
    padding: 16px;
    background: #f8f9fb;
    border-radius: 6px;
    border: 1px solid #ebeef5;

    .form-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 12px 24px;
      margin-bottom: 16px;
    }

    .form-item {
      display: flex;
      align-items: center;
      min-width: 240px;

      &.wide {
        width: 100%;
      }

      &.remark-item {
        align-items: flex-start;

        .el-textarea {
          flex: 1;
          width: auto;
        }
      }

      .el-select,
      .el-input {
        width: 180px;
      }
    }

    .form-label {
      display: inline-block;
      width: 80px;
      flex-shrink: 0;
      color: #606266;
      font-size: 13px;
    }

    .tax-label {
      margin-left: 12px;
      color: #909399;
      font-size: 12px;
    }

    .amount-summary {
      padding-top: 12px;
      border-top: 1px dashed #dcdfe6;
    }

    .amount-row {
      display: flex;
      align-items: center;
      min-height: 36px;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .amount-label {
      width: 80px;
      flex-shrink: 0;
      color: #606266;
      font-size: 13px;
    }

    .amount-value {
      font-size: 16px;
      font-weight: 600;

      &.payable {
        color: #409eff;
      }

      &.positive {
        color: #67c23a;
      }

      &.negative {
        color: #f56c6c;
      }

      &.zero {
        color: #909399;
      }
    }

    .amount-input {
      width: 180px;
    }

    .amount-tip {
      margin-left: 10px;
      color: #909399;
      font-size: 12px;
    }
  }

  .preview-tip {
    margin-top: 12px;
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
  }
}
</style>
